/**
 * UGC videos banner.
 *
 * <ugc-banner-marquee> - endless right-to-left strip of videos/images.
 * The original items are cloned until the track is wide enough to loop
 * seamlessly, then a CSS animation translates it by exactly one set width.
 * The animation only runs while the section is on screen, and each video
 * only plays while it is visible, so off-screen tiles cost nothing.
 *
 * <ugc-banner-countdown> - ticks down to a fixed end timestamp (seconds,
 * resolved in the store timezone by Liquid) and hides itself once it
 * reaches it, or once the block's visibility window ends.
 *
 * [data-ugc-banner-scroll] - button that scrolls to the first main
 * collection product grid after its banner, landing just below the fixed
 * header. Falls back to the button's link when no grid follows.
 */
(function () {
  'use strict';

  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function pad2(n) {
    return String(n).padStart(2, '0');
  }

  if (!customElements.get('ugc-banner-marquee')) {
    class UgcBannerMarquee extends HTMLElement {
      connectedCallback() {
        this.track = this.querySelector('[data-track]');
        if (!this.track) return;

        this.speed = parseFloat(this.dataset.speed) || 40;

        this.videoObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (entry.isIntersecting) {
              const playing = video.play();
              if (playing) playing.catch(() => {});
            } else {
              video.pause();
            }
          });
        });

        this.sectionObserver = new IntersectionObserver(([entry]) => {
          this.classList.toggle('is-running', entry.isIntersecting);
        });
        this.sectionObserver.observe(this);

        this.build();

        // Only the container width matters - rebuild when it grows enough to
        // need more clones (e.g. rotating a tablet), not on every resize.
        this.lastWidth = this.clientWidth;
        this.resizeObserver = new ResizeObserver(() => {
          if (Math.abs(this.clientWidth - this.lastWidth) < 1) return;
          this.lastWidth = this.clientWidth;
          window.cancelAnimationFrame(this.resizeFrame);
          this.resizeFrame = window.requestAnimationFrame(() => this.build());
        });
        this.resizeObserver.observe(this);

        // Theme editor: items can be added, removed or reordered without the
        // whole section re-rendering, so rebuild the clones when that happens.
        if (window.Shopify && window.Shopify.designMode) {
          this.mutationObserver = new MutationObserver((mutations) => {
            const external = mutations.some((mutation) =>
              [...mutation.addedNodes, ...mutation.removedNodes].some(
                (node) => node.nodeType === 1 && !node.hasAttribute('data-marquee-clone')
              )
            );
            if (external) this.build();
          });
          this.mutationObserver.observe(this.track, { childList: true });
        }

        this._onMotionChange = () => this.build();
        reducedMotion.addEventListener('change', this._onMotionChange);
      }

      disconnectedCallback() {
        if (this.videoObserver) this.videoObserver.disconnect();
        if (this.sectionObserver) this.sectionObserver.disconnect();
        if (this.resizeObserver) this.resizeObserver.disconnect();
        if (this.mutationObserver) this.mutationObserver.disconnect();
        if (this._onMotionChange) reducedMotion.removeEventListener('change', this._onMotionChange);
        window.cancelAnimationFrame(this.resizeFrame);
      }

      build() {
        if (this.mutationObserver) this.mutationObserver.disconnect();

        this.track.querySelectorAll('[data-marquee-clone]').forEach((clone) => clone.remove());
        const originals = Array.from(this.track.children);

        if (originals.length && !reducedMotion.matches) {
          this.appendSet(originals);

          // Distance between an item and its first copy = one full set,
          // including the gap after the last item.
          const firstClone = this.track.children[originals.length];
          const distance = firstClone.offsetLeft - originals[0].offsetLeft;

          // Keep enough copies that the track never runs out mid-loop.
          let sets = 1;
          while (this.track.scrollWidth < this.clientWidth + distance && sets < 20) {
            this.appendSet(originals);
            sets++;
          }

          this.style.setProperty('--ugc-banner-marquee-distance', `${distance}px`);
          this.style.setProperty('--ugc-banner-marquee-duration', `${distance / this.speed}s`);
        }

        this.videoObserver.disconnect();
        this.track.querySelectorAll('video[data-marquee-video]').forEach((video) => {
          this.videoObserver.observe(video);
        });

        if (this.mutationObserver) this.mutationObserver.observe(this.track, { childList: true });
      }

      appendSet(originals) {
        const fragment = document.createDocumentFragment();
        originals.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.setAttribute('data-marquee-clone', '');
          clone.setAttribute('aria-hidden', 'true');
          clone.removeAttribute('data-shopify-editor-block');
          fragment.appendChild(clone);
        });
        this.track.appendChild(fragment);
      }
    }

    customElements.define('ugc-banner-marquee', UgcBannerMarquee);
  }

  if (!customElements.get('ugc-banner-countdown')) {
    class UgcBannerCountdown extends HTMLElement {
      connectedCallback() {
        this.end = parseInt(this.dataset.end, 10) * 1000 || 0;
        const visibleUntil = parseInt(this.dataset.visibleUntil, 10) * 1000;
        this.hideAt = Math.min(this.end, visibleUntil || Infinity);

        this.daysEl = this.querySelector('[data-days]');
        this.hoursEl = this.querySelector('[data-hours]');
        this.minutesEl = this.querySelector('[data-minutes]');
        this.secondsEl = this.querySelector('[data-seconds]');

        // Server-rendered values can be stale from the page cache - correct
        // them straight away instead of waiting a second.
        this.tick();
        this.timer = window.setInterval(() => this.tick(), 1000);

        this._onVisibility = () => {
          if (document.visibilityState === 'visible') this.tick();
        };
        document.addEventListener('visibilitychange', this._onVisibility);
      }

      disconnectedCallback() {
        window.clearInterval(this.timer);
        if (this._onVisibility) document.removeEventListener('visibilitychange', this._onVisibility);
      }

      tick() {
        const now = Date.now();

        if (now >= this.hideAt) {
          this.hidden = true;
          window.clearInterval(this.timer);
          return;
        }

        const remaining = Math.floor((this.end - now) / 1000);
        this.daysEl.textContent = pad2(Math.floor(remaining / 86400));
        this.hoursEl.textContent = pad2(Math.floor((remaining % 86400) / 3600));
        this.minutesEl.textContent = pad2(Math.floor((remaining % 3600) / 60));
        this.secondsEl.textContent = pad2(remaining % 60);
      }
    }

    customElements.define('ugc-banner-countdown', UgcBannerCountdown);
  }

  // The split-nav menu is fixed once the page is scrolled past 200px, which
  // is always true by the time we reach the grid - so measure it up front
  // rather than reading --height-header, which is only right once fixed.
  function fixedHeaderHeight() {
    const root = document.documentElement;
    if (document.body.classList.contains('header-split-nav')) {
      const menu = document.querySelector('.header-ab-sticky');
      return menu ? menu.offsetHeight : 0;
    }
    if (root.classList.contains('sticky-header-enabled')) {
      return parseFloat(getComputedStyle(root).getPropertyValue('--height-header')) || 0;
    }
    return 0;
  }

  function findGridAfter(element) {
    const grids = document.querySelectorAll('[data-main-collection-grid]');
    return Array.from(grids).find(
      (grid) => element.compareDocumentPosition(grid) & Node.DOCUMENT_POSITION_FOLLOWING
    );
  }

  if (!window.ugcBannerScrollBound) {
    window.ugcBannerScrollBound = true;

    // Delegated so buttons re-rendered by the theme editor keep working.
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-ugc-banner-scroll]');
      if (!button) return;

      const grid = findGridAfter(button);
      if (!grid) return;

      event.preventDefault();
      const target = grid.closest('.shopify-section') || grid;
      const top = target.getBoundingClientRect().top + window.scrollY - fixedHeaderHeight();
      window.scrollTo({ top, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
    });
  }
})();
